import { useState } from "react";
import {
  User,
  Shield,
  Truck,
  Wrench,
  Factory,
  Check,
  ArrowUpRight,
} from "lucide-react";

const audiences = [
  {
    id: "particuliers",
    label: "Particuliers",
    icon: User,
    title: "Particuliers",
    subtitle: "Votre voiture, sans compromis",
    desc: "Bosses de grêle, chocs de parking, impacts légers : nous réparons votre véhicule tout en préservant sa peinture d'origine et sa valeur de revente.",
    bullets: [
      "Devis gratuit sous 24h",
      "Prise en charge assurance possible",
      "Véhicule de prêt mis à disposition",
      "Garantie à vie sur les réparations DSP",
    ],
  },
  {
    id: "assurances",
    label: "Assurances",
    icon: Shield,
    title: "Compagnies d'assurance",
    subtitle: "Partenaire stratégique sinistres climatiques",
    desc: "Nous aidons les compagnies d'assurance à gérer les sinistres grêle et environnementaux. Réparations express sous 48h : satisfaction client maximale et réduction significative des coûts.",
    bullets: [
      "Logiciel de gestion de sinistres dédié",
      "Système de mesure digital des dommages",
      "Cabines mobiles pour sinistres massifs",
      "Facturation directe tiers payant",
    ],
  },
  {
    id: "flottes",
    label: "Flottes",
    icon: Truck,
    title: "Flottes de véhicules",
    subtitle: "Réparation express pour votre parc",
    desc: "Loueurs, opérateurs logistiques, flottes d'entreprise : réduisez vos temps d'immobilisation grâce à notre réseau national et nos interventions sur site.",
    bullets: [
      "Intervention sur votre site",
      "Réparations express 48h",
      "Véhicule de prêt pour vos conducteurs",
      "Reporting et suivi digitalisé",
    ],
  },
  {
    id: "garages",
    label: "Garages",
    icon: Wrench,
    title: "Garages & concessions",
    subtitle: "Devenez Smart Center PDR France",
    desc: "Augmentez la fréquentation de votre atelier et ouvrez de nouvelles opportunités de vente croisée grâce à notre label et à notre réseau d'assureurs partenaires.",
    bullets: [
      "Clients redirigés via assureurs",
      "Formation de vos techniciens",
      "Outillage DSP professionnel",
      "Support commercial continu",
    ],
  },
  {
    id: "constructeurs",
    label: "Constructeurs",
    icon: Factory,
    title: "Marques automobiles",
    subtitle: "Solutions pour constructeurs",
    desc: "Réparation et préparation de véhicules neufs, entretien lors de salons et expositions, réparation DSP qui respecte strictement les protections d'usine d'origine.",
    bullets: [
      "Réparation avant livraison",
      "Préparation événementielle",
      "Respect des protections d'usine",
      "Partenariats long terme",
    ],
  },
];

export const Audiences = () => {
  const [active, setActive] = useState("particuliers");
  const current = audiences.find((a) => a.id === active);

  return (
    <section
      id="cibles"
      data-testid="audiences-section"
      className="relative bg-[#050505] py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#007AFF] mb-4">
            — Pour qui
          </div>
          <h2
            data-testid="audiences-title"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95]"
          >
            Le partenaire<br />
            <span className="text-[#007AFF]">idéal</span> pour tous
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-0 border border-white/10">
          {/* Tabs */}
          <div className="lg:col-span-4 bg-[#0A0A0A] border-b lg:border-b-0 lg:border-r border-white/10 flex flex-row lg:flex-col overflow-x-auto lg:overflow-visible">
            {audiences.map((a) => {
              const isActive = active === a.id;
              return (
                <button
                  key={a.id}
                  onClick={() => setActive(a.id)}
                  data-testid={`audience-tab-${a.id}`}
                  className={`flex items-center gap-4 p-6 lg:p-8 text-left border-b border-white/5 transition-all flex-shrink-0 lg:flex-shrink ${
                    isActive
                      ? "bg-[#007AFF] text-white"
                      : "hover:bg-white/5 text-white/70"
                  }`}
                >
                  <a.icon
                    className={`w-6 h-6 flex-shrink-0 ${
                      isActive ? "text-white" : "text-[#007AFF]"
                    }`}
                    strokeWidth={1.5}
                  />
                  <div>
                    <div
                      className={`text-[10px] uppercase tracking-[0.3em] ${
                        isActive ? "text-white/70" : "text-white/40"
                      }`}
                    >
                      Cible
                    </div>
                    <div className="font-heading font-bold uppercase tracking-tight text-sm md:text-base whitespace-nowrap">
                      {a.label}
                    </div>
                  </div>
                  {isActive && (
                    <ArrowUpRight className="w-4 h-4 ml-auto hidden lg:block" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div
            key={current.id}
            data-testid="audience-content"
            className="lg:col-span-8 bg-[#0A0A0A] p-8 md:p-12 lg:p-16 animate-fade-up"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#007AFF]/10 border border-[#007AFF]/30 flex items-center justify-center">
                <current.icon className="w-6 h-6 text-[#007AFF]" />
              </div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#007AFF]">
                {current.subtitle}
              </div>
            </div>

            <h3 className="font-heading text-3xl md:text-5xl font-black uppercase tracking-tighter mb-6">
              {current.title}
            </h3>
            <p className="text-base md:text-lg text-white/70 leading-relaxed mb-10 max-w-2xl">
              {current.desc}
            </p>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
              {current.bullets.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm text-white/80"
                >
                  <Check className="w-5 h-5 text-[#007AFF] flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#3B82F6] text-white text-xs font-bold uppercase tracking-widest px-6 py-3 transition-colors"
              data-testid="audience-cta"
            >
              Parler à un expert
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
